import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTheme } from "../components/ThemeContext";
import axios from "axios";
import { Form, Button, Row, Col, Table } from "react-bootstrap";
import "../App.css";
import NavMenu from "../components/NavMenu";
import { BiSolidDownload } from "react-icons/bi";
import TextareaAutosize from "react-textarea-autosize";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useLocation } from "react-router-dom";
import { IoArrowBackSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import moment from "moment";

function SubmitAppraisal() {
  const [step, setStep] = useState(1);
  const [staff, setStaff] = useState(null);
  const [factors, setFactors] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [ratings, setRatings] = useState({});
  const [comment1, setComment1] = useState("");
  const [comment2, setComment2] = useState("");
  const [comment3, setComment3] = useState("");
  const [comment4, setComment4] = useState("");
  const [employeeComment, setEmployeeComment] = useState("");
  const [staffAgreementChecked, setStaffAgreementChecked] = useState(false);
  const [supervisorAgreementChecked, setSupervisorAgreementChecked] =
    useState(false);
  const location = useLocation();
  const send_id = location.state?.send_id;
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [staffAgreement, setStaffAgreement] = useState(null);
  const isSubmitted = status.toLowerCase() === "submitted";
  const isEmployeeSigned = status.toLowerCase() === "employee signed";
  const isSigned = status.toLowerCase() === "both signed";
  // const [includeSignature, setIncludeSignature] = useState(true);
  const { isDark } = useTheme();
  const [selectedAppraisalType, setSelectedAppraisalType] = useState("");
  const [biAnnualConfirmationNotes, setBiAnnualConfirmationNotes] =
    useState("");
  // eslint-disable-next-line
  const [recommendationData, setRecommendationData] = useState(null);
  const [allow, setAllow] = useState(1);

  const part1Average = useMemo(() => {
    const part1Ratings = Object.keys(ratings)
      .filter((key) => key.startsWith("factor-"))
      .map((key) => parseFloat(ratings[key]));

    if (part1Ratings.length === 0) return 0;
    const total = part1Ratings.reduce((sum, val) => sum + val, 0);
    return (total / part1Ratings.length).toFixed(2);
  }, [ratings]);

  const part2Average = useMemo(() => {
    let totalWeightedScore = 0;
    let totalScore = 0;

    performances.forEach((item, index) => {
      const score = parseFloat(item.score);
      const rating = parseFloat(ratings[`perf-${index}`]);

      if (!isNaN(score) && !isNaN(rating)) {
        totalWeightedScore += score * rating;
        totalScore += score;
      }
    });

    if (totalScore === 0) return 0;
    return (totalWeightedScore / totalScore).toFixed(2);
  }, [ratings, performances]);

  const overallAverage = useMemo(() => {
    const p1 = parseFloat(part1Average);
    const p2 = parseFloat(part2Average);

    if (!isNaN(p1) && !isNaN(p2)) {
      return ((p1 + p2) / 2).toFixed(2);
    }
    return 0;
  }, [part1Average, part2Average]);

  const getRatingColor = (value) => {
    const rating = parseFloat(value);

    if (isNaN(rating)) return "#dee2e6"; // default grey
    if (rating >= 4.5) return "#28a745"; // green
    if (rating >= 4.0) return "#20c997"; // teal
    if (rating >= 3.0) return "#ffc107"; // yellow
    if (rating >= 2.0) return "#fd7e14"; // orange
    return "#dc3545"; // red
  };

  const getPerformanceCategory = (rating) => {
    const value = parseFloat(rating);

    if (isNaN(value)) return null;

    if (value >= 4.5) {
      return {
        label: "EXCELLENT",
        range: "4.5 to 5.0",
        description:
          "Excellent performance, well exceeded expectations. For recruiters who exceed 120% of quota.",
        color: "rgba(40, 167, 69, 0.1)", // soft green
      };
    }
    if (value >= 4.0) {
      return {
        label: "VERY GOOD",
        range: "4.0 to 4.49",
        description:
          "Very good performance and consistently exceed expectations. For recruiters who exceed 100% -119% of quota.",
        color: "rgba(23, 162, 184, 0.1)", // soft teal
      };
    }
    if (value >= 3.5) {
      return {
        label: "GOOD",
        range: "3.5 to 3.99",
        description:
          "Good performance and exceed expectations in many areas. For recruiters who exceed 85% - 100% of quota.",
        color: "rgba(255, 193, 7, 0.1)", // soft yellow
      };
    }
    if (value >= 3.0) {
      return {
        label: "SATISFACTORY",
        range: "3.0 to 3.49",
        description:
          "Satisfactory performance, meet basic expectations and need some supervision. For recruiters who achieved between 70-84% of quota.",
        color: "rgba(255, 193, 7, 0.15)", // slightly deeper yellow
      };
    }
    if (value >= 2.5) {
      return {
        label: "BELOW AVERAGE",
        range: "2.5 to 2.99",
        description:
          "Below average performance. Needs supervision in their work. Warrant P-I-P if performance remains below average for 2 consecutive quarters. Recruiters who meet 50% - 69% of quota.",
        color: "rgba(253, 126, 20, 0.1)", // soft orange
      };
    }
    return {
      label: "POOR",
      range: "< 2.5",
      description:
        "Poor performance. Need improvement and a lot of supervision in all work areas. Warrant P-I-P. Recruiters who achieved less than 50% of quota for more than 2 consecutive quarters.",
      color: "rgba(220, 53, 69, 0.1)", // soft red
    };
  };

  const category = getPerformanceCategory(overallAverage);
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isStaff = staff?.display_name === currentUser?.user_name;
  const isDisabled =
    isSubmitted || isEmployeeSigned || isSigned || (allow === 0 && isStaff);
  console.log("isDisabled:", isDisabled);

  const fetchDetailTemplate = async (sendId) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No auth token found in localStorage");
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/getPAAssessment`,
        {
          params: { send_id: sendId },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        const data = response.data.data;

        if (!data || data.length === 0) {
          console.warn("No data returned for appraisal.");
          return null;
        }

        const firstItem = data[0];

        const factorItems = data.filter((item) => item.factor !== null);
        const performanceItems = data.filter((item) => item.factor === null);

        setFactors(
          factorItems.map((item) => ({
            form_id: item.form_id,
            factor: item.factor || "",
            description: item.description || "",
            rating:
              item.rating !== null && item.rating !== ""
                ? parseInt(item.rating)
                : 0,
          })),
        );

        setPerformances(
          performanceItems.map((item) => ({
            form_id: item.form_id,
            description: item.description || "",
            score: item.weight || "",
            rating:
              item.rating !== null && item.rating !== ""
                ? parseInt(item.rating)
                : 0,
            results: item.results || "",
          })),
        );

        const initialRatings = {};
        factorItems.forEach((item, i) => {
          initialRatings[`factor-${i}`] = item.rating;
        });
        performanceItems.forEach((item, i) => {
          initialRatings[`perf-${i}`] = item.rating;
        });

        setRatings(initialRatings);
        setAllow(firstItem.allow ?? 0);
        setComment1(firstItem.comment_1 || "");
        setComment2(firstItem.comment_2 || "");
        setComment3(firstItem.comment_3 || "");
        setComment4(firstItem.comment_4 || "");
        setEmployeeComment(firstItem.staff_comment || "");

        return {
          staff_agreement: firstItem.staff_agreement,
          supervisor_agreement: firstItem.supervisor_agreement,
        };
      } else {
        console.error("Error fetching template detail:", response.data.message);
        return null;
      }
    } catch (error) {
      console.error("Error loading detail template:", error);
      return null;
    }
  };

  //getAppraisalResult
  useEffect(() => {
    const fetchResult = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getAppraisalResult`,
          {
            params: { send_id },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (res.data.success && res.data.data) {
          const agreement =
            Array.isArray(res.data.data) && res.data.data.length > 0
              ? res.data.data[0].staff_agreement
              : res.data.data.staff_agreement;

          setStaffAgreement(agreement);
        }
      } catch (err) {
        console.error("Failed to fetch appraisal result", err);
      }
    };

    fetchResult();
  }, [send_id]);

  //getAppraisalRecommendation
  useEffect(() => {
    const fetchRecommendation = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No auth token found in localStorage");
        return;
      }

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getAppraisalRecommendation`,
          {
            params: { send_id },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (res.data.success && res.data.data) {
          const recommendation =
            Array.isArray(res.data.data) && res.data.data.length > 0
              ? res.data.data[0]
              : null;

          setRecommendationData(recommendation);
          if (recommendation) {
            setSelectedAppraisalType(recommendation.type || "");
            setBiAnnualConfirmationNotes(recommendation.recommendation || "");
          }
        } else {
          console.warn("No recommendation data found for this appraisal.");
        }
      } catch (err) {
        console.error("Failed to fetch appraisal recommendation", err);
      }
    };

    fetchRecommendation();
  }, [send_id]);

  //getStaffInfo
  useEffect(() => {
    if (!send_id) return;
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No auth token found in localStorage");
    }

    const init = async () => {
      const [staffResponse, agreementValues] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API}/getStaffInfo?send_id=${send_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        ),
        fetchDetailTemplate(send_id),
      ]);

      if (
        staffResponse.data.success &&
        staffResponse.data.data.length > 0 &&
        agreementValues
      ) {
        const staffData = staffResponse.data.data[0];
        setStaff(staffData);

        const currentUser = JSON.parse(localStorage.getItem("user"))?.user_name;

        if (
          agreementValues.staff_agreement === 1 &&
          staffData.display_name === currentUser
        ) {
          setStaffAgreementChecked(true);
        } else if (
          agreementValues.supervisor_agreement === 1 &&
          staffData.display_name !== currentUser
        ) {
          setSupervisorAgreementChecked(true);
        } else {
          setStaffAgreementChecked(false);
          setSupervisorAgreementChecked(false);
        }
      }
    };

    init();
  }, [send_id]);

  //getStaffInfo
  useEffect(() => {
    if (!send_id) return;

    const fetchStaff = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getStaffInfo?send_id=${send_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (response.data.success && response.data.data.length > 0) {
          const staffData = response.data.data[0];
          setStaff(staffData);
          setStatus(staffData.status);
        } else {
          console.error("No staff data returned.");
        }
      } catch (error) {
        console.error("Error fetching staff data:", error);
      }
    };

    fetchStaff();
  }, [send_id]);

  const handleRatingChange = (key, value) => {
    setRatings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCommentChange = (index, value) => {
    const updated = [...performances];
    updated[index].results = value;
    setPerformances(updated);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSaveDraft = async () => {
    const incompleteFactor = factors.some((_, i) => {
      const val = ratings[`factor-${i}`];
      return !(val >= 1 && val <= 5);
    });

    const incompletePerformance = performances.some((_, i) => {
      const val = ratings[`perf-${i}`];
      return !(val >= 1 && val <= 5);
    });

    if (incompleteFactor || incompletePerformance) {
      alert("Please complete all Part I and Part II ratings before saving.");
      return;
    }
    const ratingData = [];

    // Part I: Competencies
    factors.forEach((f, i) => {
      const rating = ratings[`factor-${i}`];
      if (rating) {
        ratingData.push({
          form_id: f.form_id,
          rating: parseInt(rating),
          results: null,
        });
      }
    });

    // Part II: Specific Performances
    performances.forEach((p, i) => {
      const rating = ratings[`perf-${i}`];
      const score = p.score;
      if (rating) {
        ratingData.push({
          form_id: p.form_id,
          rating: parseInt(rating),
          results: p.results || "",
          weight: score || "",
        });
      }
    });

    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No auth token found in localStorage");
    }

    // Update weight in performance
    try {
      await Promise.all(
        performances.map(async (p) => {
          if (p.form_id && p.score) {
            await axios.post(
              `${process.env.REACT_APP_API}/updateWeight`,
              {
                form_id: p.form_id,
                weight: p.score,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
          }
        }),
      );
    } catch (error) {
      console.error("Error updating weights:", error);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/saveRating`,
        {
          send_id,
          ratings: ratingData,
          status: "draft",
          results: {
            part2_result: part2Average,
            comments: performances.map((p) => ({
              form_id: p.form_id,
              comment: p.results || "",
            })),
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        if (status?.toLowerCase() === "pending") {
          const updateResponse = await axios.post(
            `${process.env.REACT_APP_API}/updateAppraisalStatus`,
            {
              send_id,
              status: "reviewing",
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (updateResponse.data.success) {
            await axios.post(
              `${process.env.REACT_APP_API}/sendReviewingStatusEmail`,
              { send_id },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
          }
        }
      } else {
        alert("Failed to save draft.");
        return;
      }
    } catch (error) {
      console.error("Save failed:", error);
      alert("Error saving draft.");
      return;
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      const isStaff = staff.display_name === currentUser?.user_name;

      const payload = {
        send_id,
        part1_result: part1Average,
        part2_result: part2Average,
        overall_result: overallAverage,
        comment_1: comment1,
        comment_2: comment2,
        comment_3: comment3,
        comment_4: comment4,
        staff_comment: employeeComment,
      };

      if (isStaff) {
        payload.staff_id = currentUser?.id;
        payload.staff_agreement = staffAgreementChecked ? 1 : 0;
      } else {
        payload.supervisor_id = currentUser?.id;
        payload.supervisor_agreement = supervisorAgreementChecked ? 1 : 0;
      }

      const resultResponse = await axios.post(
        `${process.env.REACT_APP_API}/saveResult`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (resultResponse.data.success) {
        if (!isStaff) {
          try {
            const recResponse = await axios.post(
              `${process.env.REACT_APP_API}/saveRecommendation`,
              {
                send_id,
                type: selectedAppraisalType || "",
                recommendation: biAnnualConfirmationNotes || "",
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            if (!recResponse.data.success) {
              alert("Failed to save recommendation.");
            }
          } catch (error) {
            console.error("Recommendation save error:", error);
            alert("Error saving recommendation.");
          }
        }
        alert("Saved successfully!");

        if (staffAgreementChecked && isStaff) {
          if (staffAgreementChecked && isStaff) {
            await saveSignatureHistory("staff");

            // send email to manager when employee signs
            await axios.post(
              `${process.env.REACT_APP_API}/sendEmailtoManager`,
              { send_id },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
          }
        }
        if (supervisorAgreementChecked && !isStaff) {
          await saveSignatureHistory("supervisor");
        }

        await axios.post(
          `${process.env.REACT_APP_API}/saveHistory`,
          {
            send_id,
            description: "Draft appraisal saved",
            created_by: currentUser?.id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        window.location.reload();
      } else {
        alert("Failed to save.");
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Error saving result.");
    }
  };

  const saveSignatureHistory = useCallback(
    async (role) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("No auth token found in localStorage");
        }

        await axios.post(
          `${process.env.REACT_APP_API}/saveHistory`,
          {
            send_id,
            description:
              role === "staff" ? "Employee signed" : "Supervisor signed",
            created_by: currentUser?.id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        console.log(
          "Updating status to:",
          role === "staff" ? "employee signed" : "both signed",
        );
        const updateRes = await axios.post(
          `${process.env.REACT_APP_API}/updateAppraisalStatus`,
          {
            send_id,
            status: role === "staff" ? "Employee signed" : "Both signed",
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        console.log("Status update response:", updateRes.data);
      } catch (err) {
        console.error("Failed to save signature history:", err);
      }
    },
    [send_id],
  );

  const handleSubmitToHR = async () => {
    if (
      !comment1.trim() ||
      !comment2.trim() ||
      !comment3.trim() ||
      !comment4.trim() ||
      !employeeComment.trim() ||
      !biAnnualConfirmationNotes
    ) {
      alert("Please save all comment fields before submitting to HR.");
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No auth token found in localStorage");
    }

    try {
      const statusResponse = await axios.post(
        `${process.env.REACT_APP_API}/updateAppraisalStatus`,
        {
          send_id,
          status: "submitted",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const ratingResponse = await axios.post(
        `${process.env.REACT_APP_API}/updateRatingStatus`,
        {
          send_id,
          rating_status: "completed",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (statusResponse.data.success && ratingResponse.data.success) {
        await axios.post(
          `${process.env.REACT_APP_API}/saveHistory`,
          {
            send_id,
            description: "Appraisal Submitted to HR",
            created_by: currentUser?.id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        await axios.post(
          `${process.env.REACT_APP_API}/sendEmailtoHR`,
          { send_id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        window.location.reload();
        alert("Successfully submitted to HR!");
      } else {
        alert("Failed to complete submission.");
      }
    } catch (error) {
      console.error("Error during submission:", error);
      alert("Error occurred while submitting.");
    }
  };

  const handleStyledPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const margin = 15;
    let y = margin;

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Private & Confidential", 15, y, { align: "left" });
    y += 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("PERFORMANCE APPRAISAL FORM", 105, y, { align: "center" });
    y += 8;

    doc.setFontSize(12);
    doc.text((staff?.roles || "-").toUpperCase(), 105, y, { align: "center" });

    y += 5;

    // const formatDate = (dateStr) => {
    //   if (!dateStr) return "-";
    //   const date = new Date(dateStr);
    //   return `${String(date.getDate()).padStart(2, "0")}-${String(
    //     date.getMonth() + 1
    //   ).padStart(2, "0")}-${date.getFullYear()}`;
    // };

    const formatDateFull = (dateStr) => {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${date.getFullYear()}`;
    };

    const formatMonthYear = (dateStr) => {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      const month = date
        .toLocaleString("en-US", { month: "short" })
        .toUpperCase();
      const year = date.getFullYear();
      return `${month} ${year}`;
    };

    const assessmentFrom =
      staff.type === "confirmation"
        ? formatDateFull(staff.date_employed)
        : formatMonthYear(staff.start_date);

    const assessmentTo =
      staff.type === "confirmation"
        ? formatDateFull(
            moment(staff.date_employed).add(3, "months").subtract(1, "days"),
          )
        : formatMonthYear(moment(staff.due_date).subtract(1, "days"));

    // Employee Info
    if (staff) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      autoTable(doc, {
        startY: y,
        theme: "grid",
        head: [["Information", ""]],
        body: [
          ["Name", staff.display_name || "-"],
          ["Department", staff.department || "-"],
          ["Position", staff.position || "-"],
          ["Join Date", formatDateFull(staff.date_employed)],
          [
            "Date Appointed to Current Position",
            formatDateFull(staff.effective_date),
          ],
          ["Assessment Period", `${assessmentFrom} to ${assessmentTo}`],
        ],
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
      });
    }

    y = doc.lastAutoTable.finalY + 4;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: y,
      theme: "plain",
      head: [["NOTES"]],
      body: [
        [
          "1. This form is to be used for the formal recording and reporting of the staff's performance on a 6-monthly basis in June and December of the year.",
        ],
        [
          "2. The appraisal is to be carried out with reference to the following:",
        ],
        [
          "    a. Chairman's Strategic Initiatives, KPIs at Division/Department Levels",
        ],
        ["    b. Roles and Responsibilities, Job Description and Action Plan"],
        ["    c. Individual Development Plan"],
        [
          "    d. Other documents with information on technical/professional performance and results",
        ],
        [
          "3. The Open Appraisal System is encouraged. Each staff may do a Self Assessment. The Immediate Supervisor  incorporates his assessment into an Understood/Accepted Assessment which is then submitted to HR Dept. ",
        ],
        [
          "4. Both the staff and the immediate supervisor must sign on the final copy of the PA to indicate that both parties  understand and accept the outcomes of the performance review exercise.",
        ],
      ],
      styles: { fontSize: 9, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 4;

    // Notes for Part I: Competencies
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: y,
      theme: "plain",
      head: [["Part I: COMPETENCIES"]],
      body: [
        [
          "These are behaviours that the staff demonstrates to perform the job. Evaluate the extent to which the staff demonstrates these competencies in achieving the desired performance objectives contained in Part II.",
        ],
        [
          "Each appraisal factor has 5 levels: from lowest level of 1 to highest level of 5. Check against the level that is indicative of the competencies of the staff. ",
        ],
        [
          "For Part I Sub-Total, divide the total points as represented by the levels by the number of factors.",
        ],
      ],
      headStyles: {
        fontSize: 12,
        fontStyle: "bold",
        textColor: [0, 0, 0],
        halign: "center",
      },
      styles: { fontSize: 9, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 4;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Factor", "Description", "Rating"]],
      body: factors.map((f, i) => [
        `${i + 1}. ${f.factor}`,
        f.description.split(/(?=[a-e]\))/g).join("\n"), // wrap a)-e) nicely
        ratings[`factor-${i}`] || "-",
      ]),
      styles: {
        fontSize: 10,
        cellPadding: 2,
        valign: "top",
      },
      headStyles: {
        halign: "center",
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 110, fontSize: 9 },
        2: { cellWidth: 20, halign: "center" },
      },
      margin: { left: margin, right: margin },
    });

    // y = doc.lastAutoTable.finalY + 4;

    doc.addPage();

    // Part II: Specific Performance
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: 20,
      theme: "plain",
      head: [["Part II: SPECIFIC PERFORMANCE OBJECTIVES"]],
      body: [
        [
          "Work objectives are set at the beginning of each year. They are based on goals that have been identified in the Chairman' s Annual Strategic Initiatives and KPIs set for the year and respective divisions.",
        ],
        [
          "Achievement of these objectives will produce results which include revenue generation, cost reduction, and implementation of systems improvement measures.",
        ],
        [
          "Specific Performance Objectives also include individual development objectives identified as being needed to improve the individual's technical competence or commitment to superior performance.",
        ],
        [
          "These include specific technical or professional courses, seminars and continuing education programmes.",
        ],
        [
          "Results indicate the relative success of the staff when measured against the KPI or agreed targets.",
        ],
        [
          "Use weights of 3 (highly), 2 (medium) or 1 (routine/less) to indicate the relative importance of the work objective against the other objectives. Use the following to determine the level of attainment of each of the specific work objective:",
        ],
        ["Rating     Performance Assessment"],
        ["  5            Outstanding performance, far exceeding targets set"],
        ["  4            Performance consisitently above targets set"],
        ["  3            Performance meets targets set"],
        [
          "  2            Performance below targets set but within tolerance variance",
        ],
        ["  1            Performance far below targets set"],
      ],
      headStyles: {
        fontSize: 12,
        fontStyle: "bold",
        textColor: [0, 0, 0],
        halign: "center",
      },
      styles: { fontSize: 9, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 4;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["S/N", "Objective", "Weight", "Rating"]],
      body: performances.flatMap((p, i) => [
        [
          `${i + 1}`,
          (p.description || "").replace(/≥/g, ">= "),
          p.score,
          ratings[`perf-${i}`] || "-",
        ],
        [
          "", // Empty S/N
          {
            content: `Result: ${p.results || "-"}`,
            colSpan: 3,
            styles: { fontStyle: "bold", textColor: 100 },
          },
          // Empty cells for Weight and Rating, handled by colSpan
        ],
      ]),
      styles: { fontSize: 10 },
      headStyles: {
        halign: "center",
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        1: { cellWidth: 120 },
        2: { cellWidth: 20, halign: "center" },
        3: { cellWidth: 20, halign: "center" },
      },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 10;

    // OVERALL ASSESSMENT
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Part III: OVERALL ASSESSMENT", 105, y, { align: "center" });
    y += 4;

    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["Part I", "Part II", "Overall"]],
      body: [[part1Average, part2Average, overallAverage]],
      styles: { fontSize: 11, fontStyle: "bold" },
      headStyles: {
        fontSize: 12,
        fontStyle: "bold",
        textColor: [0, 0, 0],
        halign: "center",
      },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
      },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 10;

    // Dynamic values
    const label = category.label;
    const range = category.range;
    const description = category.description;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`${label} - ${range}`, margin, y);
    y += 6;

    // Description (in parentheses)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80);
    const wrappedText = doc.splitTextToSize(`(${description})`, 180);
    doc.text(wrappedText, margin, y);
    doc.setTextColor(0);
    y += 12;

    // Comments Placeholder
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Immediate Supervisor's Comments:", margin, y);
    y += 4;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [
        [
          "a) Identify strengths, accomplishments, skills and abilities of employee ",
        ],
      ],
      body: [[comment1]],
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      styles: { fontSize: 10, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 5;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["b) Clarify on areas for improvement "]],
      body: [[comment2]],
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      styles: { fontSize: 10, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 5;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["c) List competencies to develop and training needs "]],
      body: [[comment3]],
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      styles: { fontSize: 10, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 5;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [["d) Overall summary of employee’s performance "]],
      body: [[comment4]],
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      styles: { fontSize: 10, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Employee's Comments:", margin, y);
    y += 2;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    autoTable(doc, {
      startY: y,
      theme: "grid",
      head: [
        [
          "State your perception of your overall performance during this appraisal period. Include accomplishments not identified elsewhere on this form and describe skills and abilities you think need to be improved.  If you disagree with this appraisal, write your reasons here.",
        ],
      ],
      body: [[employeeComment]],
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      styles: { fontSize: 10, lineHeight: 0.2 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Understood/Accepted (U/A) Version:", margin, y);
    y += 8;

    doc.setFontSize(10);
    const wrappedText2 = doc.splitTextToSize(
      "Both the Immediate Superior and Employee have discussed the appraisal and have reached essential agreement relating to this review.  This appraisal represents an understood and accepted record by both parties.",
      180,
    );
    doc.text(wrappedText2, margin, y);
    y += wrappedText2.length * 5 + 6;

    // Show Recommendation section only if the user is not staff
    if (!isStaff) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Recommendation:", margin, y);
      y += 6;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      const boxSize = 5;
      const boxSpacing = 40;

      const isBiannual = selectedAppraisalType === "biannual";
      const isConfirmation = selectedAppraisalType === "confirmation";

      // Bi-Annual checkbox
      doc.rect(margin, y - boxSize + 1, boxSize, boxSize);
      if (isBiannual) {
        doc.setFillColor(0, 0, 0); // fill black
        doc.rect(margin + 1, y - boxSize + 2, boxSize - 2, boxSize - 2, "F");
      }
      doc.text("For Bi-Annual", margin + boxSpacing, y);

      // Confirmation checkbox
      doc.rect(margin + 90, y - boxSize + 1, boxSize, boxSize);
      if (isConfirmation) {
        doc.setFillColor(0, 0, 0); // fill black
        doc.rect(margin + 91, y - boxSize + 2, boxSize - 2, boxSize - 2, "F");
      }
      doc.text("For Confirmation", margin + 90 + boxSpacing, y);

      y += 8;

      const wrappedRecommendation = doc.splitTextToSize(
        biAnnualConfirmationNotes || "-",
        180,
      );
      doc.text(wrappedRecommendation, margin, y);
      y += wrappedRecommendation.length * 5 + 6;
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    const bottomMargin = 20; // safe margin
    const requiredSpace = 60; // estimated space needed for signature boxes + padding

    if (y + requiredSpace > pageHeight - bottomMargin) {
      doc.addPage();
      y = 20; // reset y for the new page
    }

    const boxWidth = 85;
    const boxHeight = 35;
    const gap = 10;
    const labelXOffset = 4;
    const valueXOffset = 40;
    const lineHeight = 10;

    // Left Box (Employee)
    const employeeBoxX = margin;
    doc.rect(employeeBoxX, y, boxWidth, boxHeight);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Employee Name:", employeeBoxX + labelXOffset, y + lineHeight);
    doc.text("Date:", employeeBoxX + labelXOffset, y + lineHeight * 2);
    doc.text("Signature:", employeeBoxX + labelXOffset, y + lineHeight * 3);

    // Values
    doc.setFont("helvetica", "normal");
    doc.text(
      staff?.display_name || "-",
      employeeBoxX + valueXOffset,
      y + lineHeight,
    );
    doc.text(
      staff?.dateofstaffsign
        ? moment(staff.dateofstaffsign).format("DD-MM-YYYY")
        : "-",
      employeeBoxX + valueXOffset,
      y + lineHeight * 2,
    );

    // Right Box (Supervisor)
    const supervisorBoxX = employeeBoxX + boxWidth + gap;
    doc.rect(supervisorBoxX, y, boxWidth, boxHeight);
    doc.setFont("helvetica", "bold");
    doc.text("Supervisor Name:", supervisorBoxX + labelXOffset, y + lineHeight);
    doc.text("Date:", supervisorBoxX + labelXOffset, y + lineHeight * 2);
    doc.text("Signature:", supervisorBoxX + labelXOffset, y + lineHeight * 3);

    // Values
    doc.setFont("helvetica", "normal");
    doc.text(
      staff?.supervisor_name || "-",
      supervisorBoxX + valueXOffset,
      y + lineHeight,
    );
    doc.text(
      staff?.dateofsupervisorsign
        ? moment(staff.dateofsupervisorsign).format("DD-MM-YYYY")
        : "-",
      supervisorBoxX + valueXOffset,
      y + lineHeight * 2,
    );

    // Signatures if included
    if (
      // includeSignature &&
      status.toLowerCase() === "submitted" &&
      staff?.staff_signature &&
      staff?.supervisor_signature
    ) {
      try {
        const imgWidth = 40;
        const imgHeight = 10;

        doc.addImage(
          staff.staff_signature,
          "PNG",
          employeeBoxX + valueXOffset,
          y + lineHeight * 3 - 7,
          imgWidth,
          imgHeight,
        );

        doc.addImage(
          staff.supervisor_signature,
          "PNG",
          supervisorBoxX + valueXOffset,
          y + lineHeight * 3 - 7,
          imgWidth,
          imgHeight,
        );
      } catch (err) {
        console.error("Error adding signatures to PDF:", err);
      }
    }

    // Move Y position for next section cleanly
    y += boxHeight + 10;

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);

      // Bottom-left: unique_doc_id
      doc.text(
        `Doc id: ${staff?.unique_doc_id || "-"}`,
        margin,
        doc.internal.pageSize.getHeight() - 10,
      );

      // Bottom-right: page number
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 40,
        doc.internal.pageSize.getHeight() - 10,
      );
    }

    // Save
    const cleanName = staff.display_name.replace(/[^a-z0-9]/gi, "_");
    doc.save(`Performance_Appraisal_${cleanName}.pdf`);
  };

  const checkPart1Complete = () => {
    const incomplete = factors.some((_, i) => {
      const val = ratings[`factor-${i}`];
      return !(val >= 1 && val <= 5);
    });
    return !incomplete;
  };

  const checkPart2Complete = () => {
    const incomplete = performances.some((p, i) => {
      const ratingVal = ratings[`perf-${i}`];
      const resultFilled = p.results && p.results.trim().length > 0;
      return !(ratingVal >= 1 && ratingVal <= 5 && resultFilled);
    });
    return !incomplete;
  };

  const handleWeightChange = (index, value) => {
    const updatedPerformances = [...performances];
    updatedPerformances[index].score = value;
    setPerformances(updatedPerformances);
  };

  return (
    <>
      <NavMenu />
      <div className={`p-4 ${isDark ? "text-light" : "text-dark"}`}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="header-title mb-0 d-flex align-items-center">
            <Button
              variant={isDark ? "outline-dark" : "outline-light"}
              size="sm"
              onClick={handleBack}
              style={{
                fontSize: "0.9rem",
                color: "#B31E8D",
                border: isDark ? "1px solid #202020" : "1px solid #EAECF0",
              }}
            >
              <IoArrowBackSharp
                style={{
                  marginRight: "5px",
                  color: isDark ? "#fff" : "#B31E8D",
                }}
                size={20}
              />
            </Button>
            {staff && (
              <strong style={{ marginLeft: "10px" }}>
                {staff.type === "confirmation"
                  ? "Confirmation Performance Appraisal"
                  : "Performance Appraisal"}
              </strong>
            )}

            {/* Performance Appraisal */}
          </h5>

          {status?.toLowerCase() === "submitted" ? (
            <span
              className="px-3 py-1 rounded"
              style={{
                backgroundColor: "#e6ffe6",
                color: "#2e7d32",
                fontWeight: 600,
                fontSize: "0.9rem",
                border: "1px solid #a5d6a7",
              }}
            >
              ✔ Completed
            </span>
          ) : isDisabled ? (
            <span
              className="px-3 py-1 rounded"
              style={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                fontWeight: 600,
                fontSize: "0.9rem",
                border: "1px solid #ffeeba",
              }}
            >
              {isStaff
                ? "You are not allowed to edit the PA Document"
                : "Editing is disabled"}
            </span>
          ) : null}
        </div>

        {staff && (
          <Table bordered className={`mb-4 ${isDark ? "table-dark" : ""}`}>
            <thead>
              <tr>
                <th colSpan={2}>Employee Information</th>
                <th colSpan={2} className="text-end">
                  {staff.position}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Name</td>
                <td>
                  <strong>{staff.display_name}</strong>
                </td>
                <td>Department</td>
                <td>
                  <strong>{staff.department}</strong>
                </td>
              </tr>
              <tr>
                <td>Join Date</td>
                <td>
                  <strong>
                    {moment(staff.date_employed).format("DD-MM-YYYY")}
                  </strong>
                </td>
                <td>Date Appointed to Current Position</td>
                <td>
                  <strong>
                    {moment(staff.effective_date).format("DD-MM-YYYY")}
                  </strong>
                </td>
              </tr>
              <tr>
                <td>Assessment Period From</td>
                <td>
                  <strong>
                    {staff.type === "confirmation"
                      ? moment(staff.date_employed).format("DD-MM-YYYY")
                      : moment(staff.start_date)
                          .format("MMM YYYY")
                          .toUpperCase()}
                  </strong>
                </td>
                <td>Assessment Period To</td>
                <td>
                  <strong>
                    {staff.type === "confirmation"
                      ? moment(staff.date_employed)
                          .add(3, "months")
                          .subtract(1, "days")
                          .format("DD-MM-YYYY")
                      : moment(staff.due_date)
                          .subtract(1, "days")
                          .format("MMM YYYY")
                          .toUpperCase()}
                  </strong>
                </td>
              </tr>
            </tbody>
          </Table>
        )}

        {/* Stepper */}
        <div className="stepper mb-4 d-flex align-items-center gap-2">
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div
                className={`step ${step === num ? "active" : ""} ${
                  isDark ? "step-dark" : "step-light"
                }`}
                onClick={() => {
                  if (num === 2 && !checkPart1Complete()) {
                    alert(
                      "Please complete all Part I ratings before proceeding to Part II.",
                    );
                    return;
                  }
                  if (num === 3 && !checkPart2Complete()) {
                    alert(
                      "Please complete all Part II ratings before proceeding to Part III.",
                    );
                    return;
                  }
                  setStep(num);
                }}
                style={{ cursor: "pointer" }}
              >
                {num}
              </div>
              {num < 3 && (
                <div
                  className={`line ${isDark ? "line-dark" : "line-light"}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <>
            <p className="fw-semibold">Part I: COMPETENCIES</p>
            <div
              className={`border rounded p-3 ${
                isDark
                  ? "bg-dark text-light border-secondary"
                  : "bg-light text-dark"
              }`}
            >
              <Row
                className={`fw-semibold border-bottom pb-2 mb-2 ${
                  isDark ? "border-secondary" : ""
                }`}
              >
                <Col md={3}>
                  <div className="text-center mt-1 fw-semibold">Factor</div>
                </Col>
                <Col md={6}>
                  <div className="text-center mt-1 fw-semibold">
                    Description
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center mt-1 fw-semibold">Rating</div>
                </Col>
              </Row>

              {factors.map((factor, index) => (
                <Row className="mb-3" key={index}>
                  <Col md={3}>
                    {factor.factor.includes("(") ? (
                      <>
                        <strong>
                          {index + 1}. {factor.factor.split("(")[0].trim()}
                        </strong>
                        <div
                          className={isDark ? "text-secondary" : "text-muted"}
                        >
                          ({factor.factor.split("(")[1].replace(")", "")})
                        </div>
                      </>
                    ) : (
                      <strong>{factor.factor}</strong>
                    )}
                  </Col>
                  <Col md={6}>
                    {factor.description.split(/(?=[a-e]\))/g).map((line, i) => (
                      <div key={i}>{line.trim()}</div>
                    ))}
                  </Col>
                  <Col md={3} className="mt-2">
                    <div className="d-flex flex-column align-items-center">
                      <div className="d-flex justify-content-between align-items-center">
                        {[1, 2, 3, 4, 5].map((val) => (
                          <div
                            key={val}
                            onClick={() =>
                              !isDisabled &&
                              handleRatingChange(`factor-${index}`, val)
                            }
                            className={`custom-radio-dot ${
                              ratings[`factor-${index}`] === val
                                ? "selected"
                                : ""
                            }`}
                            style={{
                              width: "20px",
                              height: "20px",
                              margin: "0 10px",
                              borderRadius: "50%",
                              backgroundColor:
                                ratings[`factor-${index}`] === val
                                  ? "#d63384"
                                  : isDark
                                    ? "#fff"
                                    : "#808b96",
                              cursor: isDisabled ? "default" : "pointer",
                            }}
                          ></div>
                        ))}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-1">
                        {[1, 2, 3, 4, 5].map((val) => (
                          <small
                            key={`label-${val}`}
                            style={{
                              width: "20px",
                              textAlign: "center",
                              margin: "0 10px",
                            }}
                          >
                            {val}
                          </small>
                        ))}
                      </div>
                    </div>
                  </Col>
                  <div
                    style={{
                      borderTop: `1px solid ${isDark ? "#444" : "#dee2e6"}`,
                      margin: "1rem 0",
                    }}
                  ></div>
                </Row>
              ))}
            </div>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="outline-light"
                onClick={() => {
                  if (!checkPart1Complete()) {
                    alert(
                      "Please complete all Part I ratings before proceeding.",
                    );
                    return;
                  }
                  setStep(2);
                }}
                style={{ borderColor: "#B31E8D", color: "#B31E8D" }}
              >
                Next Page
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="fw-semibold">
              Part II: SPECIFIC PERFORMANCE OBJECTIVES
            </p>
            <div
              className={`border rounded p-3 ${
                isDark
                  ? "bg-dark text-light border-secondary"
                  : "bg-light text-dark"
              }`}
            >
              <Row
                className={`fw-semibold border-bottom pb-2 mb-2 ${
                  isDark ? "border-secondary" : ""
                }`}
              >
                <Col md={1} className="text-center">
                  S/N
                </Col>
                <Col md={6}>Description</Col>
                <Col md={1} className="text-center">
                  Weight
                </Col>
                <Col md={4}>
                  <div className="text-center mt-1 fw-semibold">Rating</div>
                </Col>
              </Row>

              {performances.map((item, index) => (
                <Row className="mb-3" key={index}>
                  <Col md={1} className="text-center">
                    <p>{index + 1}. </p>
                  </Col>
                  <Col md={6}>
                    {item.description.split("•").map(
                      (line, idx) =>
                        line.trim() && (
                          <div key={idx}>
                            {idx > 0 && (
                              <span
                                style={{ marginRight: "6px", color: "#B31E8D" }}
                              >
                                •
                              </span>
                            )}
                            {line.trim()}
                          </div>
                        ),
                    )}
                    <div className="d-flex align-items-start mt-2">
                      <label
                        className="me-2 mt-1"
                        style={{
                          minWidth: "60px",
                          fontWeight: "bold",
                          color: isDark ? "#fff" : "#000",
                        }}
                      >
                        Result:
                      </label>
                      <textarea
                        className="form-control"
                        placeholder="Enter the results..."
                        rows={2}
                        style={{
                          backgroundColor: isDisabled
                            ? isDark
                              ? "#2e2e2e"
                              : "#f8f9fa"
                            : isDark
                              ? "#282828"
                              : "#ffffff",
                          color: isDisabled
                            ? isDark
                              ? "#aaa"
                              : "#6c757d"
                            : isDark
                              ? "#fff"
                              : "#000",
                          borderColor: isDark ? "#444" : "#ced4da",
                          cursor: isDisabled ? "not-allowed" : "text",
                        }}
                        value={item.results || ""}
                        onChange={(e) =>
                          handleCommentChange(index, e.target.value)
                        }
                        // disabled={isSubmitted}
                        readOnly={isDisabled}
                      />
                    </div>
                  </Col>

                  <Col md={1} className="text-center">
                    <Form.Select
                      size="sm"
                      value={item.score}
                      onChange={(e) =>
                        handleWeightChange(index, e.target.value)
                      }
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDark ? "#2e2e2e" : "#fff",
                        color: isDark ? "#fff" : "#000",
                        borderColor: isDark ? "#555" : "#ced4da",
                      }}
                    >
                      <option value="">-</option>
                      {[1, 2, 3].map((val) => (
                        <option key={val} value={val}>
                          {val}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4} className="mt-2">
                    <div className="d-flex flex-column align-items-center">
                      <div className="d-flex justify-content-between align-items-center">
                        {[1, 2, 3, 4, 5].map((val) => (
                          <div
                            key={val}
                            onClick={() =>
                              !isDisabled &&
                              handleRatingChange(`perf-${index}`, val)
                            }
                            className={`custom-radio-dot ${
                              ratings[`perf-${index}`] === val ? "selected" : ""
                            }`}
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              margin: "0 10px",
                              backgroundColor:
                                ratings[`perf-${index}`] === val
                                  ? "#d63384"
                                  : isDark
                                    ? "#fff"
                                    : "#808b96",
                              cursor: isDisabled ? "default" : "pointer",
                            }}
                          ></div>
                        ))}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        {[1, 2, 3, 4, 5].map((val) => (
                          <small
                            key={`label-${val}`}
                            style={{
                              width: "20px",
                              textAlign: "center",
                              margin: "0 10px",
                            }}
                          >
                            {val}
                          </small>
                        ))}
                      </div>
                    </div>
                  </Col>

                  <div
                    style={{
                      borderTop: `1px solid ${isDark ? "#444" : "#dee2e6"}`,
                      margin: "1rem 0",
                    }}
                  ></div>
                </Row>
              ))}
            </div>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="outline-light"
                onClick={() => {
                  if (!checkPart2Complete()) {
                    alert(
                      "Please complete all Part II ratings and fill in all Result fields before proceeding.",
                    );
                    return;
                  }
                  setStep(3);
                }}
                style={{ borderColor: "#B31E8D", color: "#B31E8D" }}
              >
                Next Page
              </Button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <p className="fw-semibold">Part III: OVERALL ASSESSMENT</p>
            <p className="fw-semibold">Summary of Ratings</p>
            <p>Overall Rating: Mean of Part I & II</p>

            <Row>
              <Col md={6}>
                <div className="d-flex align-items-center gap-4 mb-3">
                  <div className="d-flex gap-4">
                    {[part1Average, part2Average, overallAverage].map(
                      (avg, i) => (
                        <div
                          key={i}
                          className={`rounded-circle text-center d-flex flex-column justify-content-center align-items-center ${
                            isDark
                              ? "bg-dark text-light border-secondary"
                              : "bg-light text-dark"
                          }`}
                          style={{
                            width: 100,
                            height: 100,
                            border: `3px solid ${getRatingColor(avg)}`,
                            fontWeight: 600,
                          }}
                        >
                          <div
                            style={{
                              color: getRatingColor(avg),
                              fontSize: "1.3rem",
                            }}
                          >
                            {avg}
                          </div>
                          <div
                            className={isDark ? "text-secondary" : "text-muted"}
                            style={{ fontSize: "0.9rem" }}
                          >
                            {i === 0
                              ? "Part I"
                              : i === 1
                                ? "Part II"
                                : "Overall"}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </Col>

              <Col md={6} className="mb-3">
                {category && (
                  <div
                    style={{
                      background:
                        category.color || (isDark ? "#333" : "#f8f9fa"),
                      padding: "1rem",
                      borderRadius: "10px",
                      color: isDark ? "#fff" : "#000",
                    }}
                  >
                    <p className="mb-0 fw-semibold mb-2">
                      {category.label} - {category.range}
                    </p>
                    <p className="text-secondary small mb-0">
                      ({category.description})
                    </p>
                  </div>
                )}
              </Col>
            </Row>

            <Table
              bordered
              hover
              responsive
              className={`mb-4 mt-3 ${isDark ? "table-dark" : ""}`}
            >
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th>Label</th>
                  <th>Score Range</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    label: "EXCELLENT",
                    range: "4.5 to 5.0",
                    description:
                      "Excellent performance, well exceeded expectations. For recruiters who exceed 120% of quota.",
                  },
                  {
                    label: "VERY GOOD",
                    range: "4.0 to 4.49",
                    description:
                      "Very good performance and consistently exceed expectations. For recruiters who exceed 100% -119% of quota.",
                  },
                  {
                    label: "GOOD",
                    range: "3.5 to 3.99",
                    description:
                      "Good performance and exceed expectations in many areas. For recruiters who exceed 85% - 100% of quota.",
                  },
                  {
                    label: "SATISFACTORY",
                    range: "3.0 to 3.49",
                    description:
                      "Satisfactory performance, meet basic expectations and need some supervision. For recruiters who achieved between 70-84% of quota.",
                  },
                  {
                    label: "BELOW AVERAGE",
                    range: "2.5 to 2.99",
                    description:
                      "Below average performance. Needs supervision in their work. Warrant P-I-P if performance remains below average for 2 consecutive quarters. Recruiters who meet 50% - 69% of quota.",
                  },
                  {
                    label: "POOR",
                    range: "< 2.5",
                    description:
                      "Poor performance. Need improvement and a lot of supervision in all work areas. Warrant P-I-P. Recruiters who achieved less than 50% of quota for more than 2 consecutive quarters.",
                  },
                ].map((item, idx) => (
                  <tr key={idx}>
                    <td className="fw-semibold">{item.label}</td>
                    <td className="fw-semibold">{item.range}</td>
                    <td className="text-secondary">{item.description}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Supervisor's Comments */}
            <p className="fw-semibold">Immediate Supervisor's Comments:</p>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    a) Identify strengths, accomplishments, skills and abilities
                    of employee
                  </Form.Label>
                  <TextareaAutosize
                    className={`form-control ${
                      isStaff ? "readonly-textarea" : ""
                    }`}
                    minRows={2}
                    maxRows={10}
                    value={comment1}
                    onChange={(e) => setComment1(e.target.value)}
                    // readOnly={isStaff || isSubmitted}
                    readOnly={isStaff || isDisabled}
                    style={{
                      backgroundColor:
                        isStaff || isDisabled //isSubmitted
                          ? isDark
                            ? "#2e2e2e"
                            : "#f8f9fa"
                          : isDark
                            ? "#282828"
                            : "#ffffff",
                      color:
                        isStaff || isDisabled //isSubmitted
                          ? isDark
                            ? "#aaa"
                            : "#6c757d"
                          : isDark
                            ? "#fff"
                            : "#000",
                      borderColor: isDark ? "#444" : "#ced4da",
                      cursor: isStaff || isDisabled ? "not-allowed" : "text",
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>b) Clarify on areas for improvement</Form.Label>
                  <TextareaAutosize
                    className={`form-control ${
                      isStaff ? "readonly-textarea" : ""
                    }`}
                    minRows={2}
                    maxRows={10}
                    value={comment2}
                    onChange={(e) => setComment2(e.target.value)}
                    // readOnly={isStaff || isSubmitted}
                    readOnly={isStaff || isDisabled}
                    style={{
                      backgroundColor:
                        isStaff || isDisabled
                          ? isDark
                            ? "#2e2e2e"
                            : "#f8f9fa"
                          : isDark
                            ? "#282828"
                            : "#ffffff",
                      color:
                        isStaff || isDisabled
                          ? isDark
                            ? "#aaa"
                            : "#6c757d"
                          : isDark
                            ? "#fff"
                            : "#000",
                      borderColor: isDark ? "#444" : "#ced4da",
                      cursor: isStaff || isDisabled ? "not-allowed" : "text",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    c) List competencies to develop and training needs
                  </Form.Label>
                  <TextareaAutosize
                    className={`form-control ${
                      isStaff ? "readonly-textarea" : ""
                    }`}
                    minRows={2}
                    maxRows={10}
                    value={comment3}
                    onChange={(e) => setComment3(e.target.value)}
                    readOnly={isStaff || isDisabled}
                    style={{
                      backgroundColor:
                        isStaff || isDisabled
                          ? isDark
                            ? "#2e2e2e"
                            : "#f8f9fa"
                          : isDark
                            ? "#282828"
                            : "#ffffff",
                      color:
                        isStaff || isDisabled
                          ? isDark
                            ? "#aaa"
                            : "#6c757d"
                          : isDark
                            ? "#fff"
                            : "#000",
                      borderColor: isDark ? "#444" : "#ced4da",
                      cursor: isStaff || isDisabled ? "not-allowed" : "text",
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    d) Overall summary of employee's performance
                  </Form.Label>
                  <TextareaAutosize
                    className={`form-control ${
                      isStaff ? "readonly-textarea" : ""
                    }`}
                    minRows={2}
                    maxRows={10}
                    value={comment4}
                    onChange={(e) => setComment4(e.target.value)}
                    readOnly={isStaff || isDisabled}
                    style={{
                      backgroundColor:
                        isStaff || isDisabled
                          ? isDark
                            ? "#2e2e2e"
                            : "#f8f9fa"
                          : isDark
                            ? "#282828"
                            : "#ffffff",
                      color:
                        isStaff || isDisabled
                          ? isDark
                            ? "#aaa"
                            : "#6c757d"
                          : isDark
                            ? "#fff"
                            : "#000",
                      borderColor: isDark ? "#444" : "#ced4da",
                      cursor: isStaff || isDisabled ? "not-allowed" : "text",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Employee's Comments */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                Employee's Comments:
              </Form.Label>
              <Form.Text className="d-block mb-2 text-secondary small">
                State your perception of your overall performance during this
                appraisal period. Include accomplishments not identified
                elsewhere on this form and describe skills and abilities you
                think need to be related to this review. This appraisal
                represents an understood and accepted record by both parties.
              </Form.Text>
              <TextareaAutosize
                className={`form-control ${
                  !isStaff ? "readonly-textarea" : ""
                }`}
                minRows={2}
                maxRows={10}
                value={employeeComment}
                onChange={(e) => setEmployeeComment(e.target.value)}
                readOnly={
                  !isStaff || isSubmitted || isEmployeeSigned || isSigned
                }
                style={{
                  backgroundColor:
                    !isStaff || isSubmitted || isEmployeeSigned || isSigned
                      ? isDark
                        ? "#2e2e2e"
                        : "#f8f9fa"
                      : isDark
                        ? "#282828"
                        : "#ffffff",
                  color:
                    !isStaff || isSubmitted || isEmployeeSigned || isSigned
                      ? isDark
                        ? "#aaa"
                        : "#6c757d"
                      : isDark
                        ? "#fff"
                        : "#000",
                  borderColor: isDark ? "#444" : "#ced4da",
                  cursor:
                    !isStaff || isSubmitted || isEmployeeSigned || isSigned
                      ? "not-allowed"
                      : "text",
                }}
              />
            </Form.Group>

            {staff?.display_name !==
              JSON.parse(localStorage.getItem("user"))?.user_name && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">Recommendation</Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    name="appraisalType"
                    label="For Bi-annual"
                    id="biAnnual"
                    checked={selectedAppraisalType === "biannual"}
                    onChange={() => setSelectedAppraisalType("biannual")}
                    disabled={isSubmitted}
                  />
                  <Form.Check
                    type="radio"
                    name="appraisalType"
                    label="For Confirmation"
                    id="confirmation"
                    checked={selectedAppraisalType === "confirmation"}
                    onChange={() => setSelectedAppraisalType("confirmation")}
                    disabled={isSubmitted}
                    className="mt-2"
                  />
                </div>

                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Add your notes here..."
                  className="mt-3"
                  value={biAnnualConfirmationNotes}
                  onChange={(e) => setBiAnnualConfirmationNotes(e.target.value)}
                  disabled={!selectedAppraisalType || isSubmitted}
                  style={{
                    backgroundColor: isDark ? "#2e2e2e" : "#ffffff",
                    color: isDark ? "#ffffff" : "#000000",
                    borderColor: isDark ? "#555555" : "#ced4da",
                  }}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-4">
              {staff.display_name ===
              JSON.parse(localStorage.getItem("user"))?.user_name ? (
                <Form.Check
                  type="checkbox"
                  label="I understand the appraisal form (Once your supervisor has added comments, you’ll be able to check the appraisal form)"
                  checked={staffAgreementChecked}
                  onChange={(e) => setStaffAgreementChecked(e.target.checked)}
                  disabled={
                    staffAgreementChecked ||
                    isSubmitted ||
                    !comment1.trim() ||
                    !comment2.trim() ||
                    !comment3.trim() ||
                    !comment4.trim() ||
                    !employeeComment.trim()
                  }
                />
              ) : (
                <>
                  <Form.Check
                    type="checkbox"
                    label="Ready to submit"
                    checked={supervisorAgreementChecked}
                    onChange={(e) =>
                      setSupervisorAgreementChecked(e.target.checked)
                    }
                    disabled={
                      supervisorAgreementChecked ||
                      staffAgreement !== 1 ||
                      isSubmitted
                    }
                  />
                  {staffAgreement === 1 ? (
                    <div
                      className="alert alert-success mt-2 py-2 px-3 mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Employee have signed the Appraisal
                    </div>
                  ) : (
                    <div
                      className="alert alert-danger mt-2 py-2 px-3 mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Employee have not signed yet
                    </div>
                  )}
                </>
              )}

              {(staffAgreementChecked || supervisorAgreementChecked) && (
                <div
                  className="alert alert-success mt-2 py-2 px-3 mb-0"
                  style={{ fontSize: "0.9rem" }}
                >
                  You have already confirmed your agreement. Don't forget to
                  save the document.{" "}
                  <span
                    onClick={() => navigate("/signature")}
                    style={{
                      color: "#B31E8D",
                      cursor: "pointer",
                      marginLeft: "8px",
                    }}
                  >
                    Click here to save your signature
                  </span>
                </div>
              )}
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="outline-light"
                  style={{
                    background: status !== "pending" ? "#B31E8D" : "#ccc",
                    color: "white",
                    border: "none",
                  }}
                  onClick={handleStyledPDF}
                  disabled={status === "pending"}
                  title={
                    status !== "submitted"
                      ? "PDF download is only available when status is 'submitted'."
                      : ""
                  }
                >
                  <BiSolidDownload size={20} /> &nbsp;Download PDF
                </Button>

                {["pending", "reviewing"].includes(status.toLowerCase()) && (
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: "#856404",
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeeba",
                      padding: "2px 8px",
                      borderRadius: "5px",
                      fontWeight: "600",
                    }}
                  >
                    Draft
                  </span>
                )}

                {/* <Form.Check
                  type="switch"
                  id="include-signature-toggle"
                  label="Included Signature"
                  checked={includeSignature}
                  onChange={(e) => setIncludeSignature(e.target.checked)}
                /> */}
              </div>

              <div className="d-flex gap-3">
                <Button
                  variant="outline-light"
                  style={{ border: "2px solid #B31E8D", color: "#B31E8D" }}
                  onClick={handleSaveDraft}
                  disabled={isSubmitted || isSigned}
                >
                  Save Document
                </Button>

                {staff?.display_name !==
                  JSON.parse(localStorage.getItem("user"))?.user_name && (
                  <Button
                    variant="outline-light"
                    style={{ background: "#B31E8D", color: "white" }}
                    onClick={handleSubmitToHR}
                    disabled={!isSigned}
                  >
                    Submit to HR
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default SubmitAppraisal;
